/** Anzeigeformate. Lokale Zeiten kommen als "yyyy-MM-ddTHH:mm" bzw. "yyyy-MM-dd" in der Familienzeitzone. */

const weekday = new Intl.DateTimeFormat("de-DE", { weekday: "long", timeZone: "UTC" });
const shortDate = new Intl.DateTimeFormat("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", timeZone: "UTC" });
const longDate = new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

function asUtcDate(localDate: string): Date {
  return new Date(`${localDate.slice(0, 10)}T00:00:00Z`);
}

export const datePart = (local: string): string => local.slice(0, 10);

export const timePart = (local: string): string | null => (local.length > 10 ? local.slice(11, 16) : null);

export const formatWeekday = (localDate: string): string => weekday.format(asUtcDate(localDate));

export const formatShortDate = (localDate: string): string => shortDate.format(asUtcDate(localDate));

export const formatLongDate = (localDate: string): string => longDate.format(asUtcDate(localDate));

export function addDays(localDate: string, days: number): string {
  const d = asUtcDate(localDate);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Montag der Woche (ISO). */
export function startOfWeek(localDate: string): string {
  const d = asUtcDate(localDate);
  const dow = (d.getUTCDay() + 6) % 7;
  return addDays(localDate, -dow);
}

/** Heutiges Datum in einer IANA-Zeitzone. */
export function todayIn(timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

/** UTC-Grenzen eines lokalen Datumsbereichs (großzügig, Serverseite filtert exakt). */
export function utcRange(fromLocalDate: string, toLocalDateExclusive: string): { from: string; to: string } {
  const from = new Date(`${fromLocalDate}T00:00:00Z`);
  from.setUTCHours(from.getUTCHours() - 14);
  const to = new Date(`${toLocalDateExclusive}T00:00:00Z`);
  to.setUTCHours(to.getUTCHours() + 14);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function formatTimeRange(start: string, end: string | null | undefined, allDay: boolean): string {
  if (allDay) return "ganztägig";
  const s = timePart(start);
  const e = end ? timePart(end) : null;
  if (!e) return `${s} Uhr · Ende offen`;
  return datePart(start) === datePart(end!) ? `${s}–${e} Uhr` : `${s} Uhr – ${formatShortDate(end!)} ${e} Uhr`;
}

/** Anzahl ist immer gepflegt: „2×“ ohne Einheit, „500 g“ mit Einheit; fehlt sie, gilt 1. */
export function formatQuantity(quantity: number | string | null | undefined, unit: string | null | undefined): string {
  const q = quantity === null || quantity === undefined || quantity === "" ? 1 : typeof quantity === "string" ? Number(quantity) : quantity;
  return `${q.toLocaleString("de-DE", { maximumFractionDigits: 3 })}${unit ? " " + unit : "×"}`;
}

/** Eingabe der Anzahl: leer → 1; sonst positive Zahl (Komma erlaubt), sonst null = ungültig. */
export function parseQuantity(input: string): number | null {
  const t = input.trim().replace(",", ".");
  if (t === "") return 1;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 && n <= 100000 ? n : null;
}

export function relativeTime(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "gerade eben";
  if (diff < 3600) return `vor ${Math.round(diff / 60)} Min.`;
  if (diff < 86400) return `vor ${Math.round(diff / 3600)} Std.`;
  return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

/** ISO-Kalenderwoche eines lokalen Datums. */
export function isoWeek(localDate: string): number {
  const d = asUtcDate(localDate);
  d.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7));
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((d.getTime() - firstThursday.getTime()) / 86_400_000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
}

const monthYear = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric", timeZone: "UTC" });

/** „Oktober 2026“ */
export const formatMonthYear = (localDate: string): string => monthYear.format(asUtcDate(localDate));

/** Erster Tag des Monats eines lokalen Datums. */
export const startOfMonth = (localDate: string): string => `${localDate.slice(0, 7)}-01`;

/** Erster Tag des Monats, um `months` Monate verschoben. */
export function addMonths(localDate: string, months: number): string {
  const d = asUtcDate(startOfMonth(localDate));
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}
